/**
 * Centralized PostHog analytics layer for HeirWise.
 *
 * Design rules (see docs/heirwise-posthog-integration.md):
 *  - Initialized exactly once, with `defaults` pinned so future SDK default
 *    changes cannot silently alter HeirWise behavior.
 *  - Additive: GA4, Meta Pixel and Meta CAPI keep working untouched.
 *  - Never throws, never blocks UI. Silent no-op when the token is absent.
 *  - Autocapture + session replay + heatmaps ON, but privacy-constrained.
 *  - `$pageview` is captured manually by PostHogRouteTracker only.
 *  - Identity is the Supabase user UUID only — no email/name/household ever.
 */

import posthog from "posthog-js";
import { redactUrl, sanitizeAnalyticsProps } from "@/lib/analytics/privacy";

const TOKEN = import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_API_KEY as string | undefined;
const REGION = (import.meta.env.VITE_LOVABLE_CONNECTOR_POSTHOG_REGION as string | undefined) || "us";
export const POSTHOG_HOST = REGION === "eu" ? "https://eu.i.posthog.com" : "https://us.i.posthog.com";

let initialized = false;

/**
 * Routes where behavioral capture is suppressed entirely: session replay is
 * stopped AND autocapture is ignored. Deliberately coded, sanitized milestone
 * events (e.g. "login succeeded") may still be emitted from these routes.
 */
export const SENSITIVE_ROUTE_PATTERNS: RegExp[] = [
  /^\/login/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/create-username/,
  /^\/settings\/security/,
  /^\/profile/,
  /^\/account/,
  /^\/parent/,
  /^\/household/,
  /^\/support/,
  /^\/feedback/,
  /^\/data-deletion/,
  /^\/parental-consent/,
  /^\/unsubscribe/,
  /^\/admin/,
  /^\/\.lovable\/oauth/,
];

export function isSensitiveRoute(pathname: string): boolean {
  return SENSITIVE_ROUTE_PATTERNS.some((re) => re.test(pathname));
}

/**
 * Elements whose *text* is visually redacted in replay but which remain fully
 * available to autocapture and heatmaps (behavioral data preserved).
 */
const MASK_TEXT_SELECTOR = [
  ".ph-mask",
  "[data-ph-mask]",
  ".financial-value",
  "[data-sensitive-value]",
].join(", ");

/**
 * Elements removed from BOTH replay and autocapture. Reserved for areas where
 * we want zero behavioral capture.
 */
const NO_CAPTURE_SELECTORS = [
  ".ph-no-capture",
  "[data-ph-no-capture]",
  ".ph-no-autocapture",
  "[data-ph-no-autocapture]",
  ".ph-sensitive",
];

export function isPostHogReady(): boolean {
  return initialized;
}

export function initPostHog(): void {
  if (initialized || typeof window === "undefined") return;
  if (!TOKEN) return; // silent no-op — analytics must never break the app
  try {
    posthog.init(TOKEN, {
      api_host: POSTHOG_HOST,
      // Pinned configuration snapshot — prevents future SDK default changes
      // from silently altering HeirWise behavior.
      defaults: "2026-05-30",

      // --- Pageviews: exactly one manual path (PostHogRouteTracker) ---
      capture_pageview: false,
      capture_pageleave: true,

      // --- Autocapture: ON (heatmaps, dead/rage clicks, paths, Toolbar) ---
      autocapture: {
        url_ignorelist: SENSITIVE_ROUTE_PATTERNS,
        css_selector_ignorelist: NO_CAPTURE_SELECTORS,
        element_attribute_ignorelist: [
          "value",
          "placeholder",
          "title",
          "alt",
          "aria-label",
          "data-email",
          "data-user",
          "data-value",
        ],
      },
      // Note: this SDK version does not expose a `capture_copied_text` option;
      // copied-text capture is not enabled by any config we set here.
      rageclick: true,

      // --- Session replay: ON with strict privacy ---
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: MASK_TEXT_SELECTOR,
        blockSelector: NO_CAPTURE_SELECTORS.join(", "),
        // Redact URLs before anything leaves the device; never record bodies.
        maskCapturedNetworkRequestFn: (request) => ({
          ...request,
          name: redactUrl(request.name) ?? request.name,
          requestBody: undefined,
          responseBody: undefined,
          requestHeaders: undefined,
          responseHeaders: undefined,
        }),
      },

      // --- Identity ---
      person_profiles: "identified_only",

      // --- Global safety net: applies to PostHog-generated events too ---
      before_send: (event) => {
        if (!event) return null;
        try {
          const props = event.properties ?? {};
          const cleaned: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(props)) {
            // PostHog's own `$`-prefixed properties are structural — keep them,
            // but redact any URL-bearing ones.
            if (k.startsWith("$")) {
              cleaned[k] =
                typeof v === "string" && (k.includes("url") || k.includes("referrer") || k.includes("pathname"))
                  ? redactUrl(v)
                  : v;
              continue;
            }
            cleaned[k] = v;
          }
          const custom = sanitizeAnalyticsProps(
            Object.fromEntries(Object.entries(cleaned).filter(([k]) => !k.startsWith("$"))),
          );
          event.properties = {
            ...Object.fromEntries(Object.entries(cleaned).filter(([k]) => k.startsWith("$"))),
            ...custom,
          };
        } catch {
          /* never block transmission logic on sanitization errors */
        }
        return event;
      },
    });
    initialized = true;
    // The PostHog Toolbar (heatmaps, no-code actions, element inspector) and
    // debugging both look for `window.posthog`. The module import alone does
    // not create it, so expose the same instance explicitly.
    (window as unknown as { posthog?: typeof posthog }).posthog = posthog;
  } catch {
    /* analytics failures must never degrade HeirWise */
  }
}

/** Fire a product event. Properties are sanitized before transmission. */
export function trackPostHogEvent(eventName: string, props: Record<string, unknown> = {}): void {
  try {
    if (!initialized) return;
    posthog.capture(eventName, sanitizeAnalyticsProps(props));
  } catch {
    /* noop */
  }
}

/** Sole source of PostHog `$pageview` events (see PostHogRouteTracker). */
export function capturePageview(path?: string): void {
  try {
    if (!initialized || typeof window === "undefined") return;
    posthog.capture("$pageview", {
      $current_url: redactUrl(window.location.href),
      $pathname: path ?? window.location.pathname,
    });
  } catch {
    /* noop */
  }
}

/** Identify by opaque Supabase UUID only. No PII person properties. */
export function identifyUser(userId: string, safeProps: Record<string, unknown> = {}): void {
  try {
    if (!initialized || !userId) return;
    posthog.identify(userId, sanitizeAnalyticsProps(safeProps));
  } catch {
    /* noop */
  }
}

/**
 * Flags disposable QA / internal test sessions so they can be excluded from
 * production product analytics (PostHog "filter test accounts" on
 * `is_qa_test`). Only the boolean leaves the device — never the email.
 */
export function markTestAccount(isTestAccount: boolean): void {
  try {
    if (!initialized) return;
    posthog.register({ is_qa_test: isTestAccount });
    posthog.setPersonProperties({ is_qa_test: isTestAccount });
  } catch {
    /* noop */
  }
}

/** Full identity reset — shared devices must not inherit another user. */
export function resetIdentity(): void {
  try {
    if (!initialized) return;
    posthog.reset(true);
  } catch {
    /* noop */
  }
}

/** Handled errors / error boundaries. Context is sanitized. */
export function captureError(error: unknown, context: Record<string, unknown> = {}): void {
  try {
    if (!initialized) return;
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, {
      ...sanitizeAnalyticsProps(context),
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
    });
  } catch {
    /* noop */
  }
}

// --- Session replay control (used by PostHogReplayGuard) ---

export function stopSessionRecording(): void {
  try {
    if (initialized) posthog.stopSessionRecording();
  } catch {
    /* noop */
  }
}

export function startSessionRecording(): void {
  try {
    if (initialized) posthog.startSessionRecording();
  } catch {
    /* noop */
  }
}

// --- Feature flags / experiments ---
// Flags are for product rollout and experimentation ONLY. They must never
// replace server-side authorization: BetaGate, RoleGuard, billing entitlement,
// parent/child boundaries and Supabase RLS remain authoritative.

export function isFeatureEnabled(key: string, fallback = false): boolean {
  try {
    if (!initialized) return fallback;
    const value = posthog.isFeatureEnabled(key);
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

export function getFeatureFlag(key: string, fallback: string | boolean = false): string | boolean {
  try {
    if (!initialized) return fallback;
    const value = posthog.getFeatureFlag(key);
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

export function getFeatureFlagPayload<T = unknown>(key: string, fallback: T | null = null): T | null {
  try {
    if (!initialized) return fallback;
    const payload = posthog.getFeatureFlagPayload(key);
    return (payload as T) ?? fallback;
  } catch {
    return fallback;
  }
}

export function onFeatureFlags(cb: () => void): () => void {
  try {
    if (!initialized) return () => {};
    return posthog.onFeatureFlags(cb) ?? (() => {});
  } catch {
    return () => {};
  }
}

export { posthog };
